// Frontend RBAC permission utilities.
//
// Permissions are managed exclusively through User Groups + Permission Profiles
// on the backend. The `role` field is a display label only. The only exception
// is `tenant_owner` — the bootstrap superuser who always has every permission.

import { getLocalized } from '../i18n/getLocalized';

// Is this tab a delegation (Vekalet) session? Read straight from the flag
// services/api sets when it stores the session, rather than importing from
// there — this module is a pure util and must not pull axios into its tests.
function isDelegatedSession() {
  try { return sessionStorage.getItem('isDelegation') === 'true'; }
  catch { return false; }
}

/**
 * Build a user-facing message for a MISSING_PERMISSION 403 (the authorize
 * middleware's structured response), naming the missing permission and the
 * groups/roles that would grant it. Returns null for any other error shape
 * so callers can fall through to their own handling.
 */
export function missingPermissionMessage(data, t, lang) {
  if (data?.code !== 'MISSING_PERMISSION') return null;
  const permission = (data.required || [])
    .map((p) => t(`permissions.permLabels.${p}`, { defaultValue: p }))
    .join(', ');
  let msg = t('errors.missingPermission', { permission });
  const grantingRoles = data.grantingRoles || [];
  const groups = [...new Set(
    grantingRoles.flatMap((r) => r.userGroups || []).filter(Boolean)
  )];
  const roles = grantingRoles
    .map((r) => getLocalized(r.name, lang))
    .filter(Boolean);
  if (groups.length) {
    msg += ' ' + t('errors.missingPermissionGroups', { groups: groups.join(', ') });
  } else if (roles.length) {
    msg += ' ' + t('errors.missingPermissionRoles', { roles: roles.join(', ') });
  }
  return msg;
}

/**
 * Check if a user has a specific permission.
 * Reads from the effectivePermissions array returned by /auth/me, which the
 * backend computes from the user's UserGroup memberships (plus the tenant_owner
 * bootstrap escape hatch).
 */
export function hasPermission(role, permission, effectivePermissions = null) {
  if (role === 'tenant_owner' && !isDelegatedSession()) return true;
  if (!effectivePermissions) return false;
  return effectivePermissions.includes(permission);
}

/**
 * Check if a user has any of the given permissions.
 */
export function hasAnyPermission(role, permissions, effectivePermissions = null) {
  // The tenant_owner escape hatch must not survive into a delegation session.
  // There the session identity is the delegator — owner role and all — but the
  // backend has already narrowed effectivePermissions to what the delegation
  // grants. Short-circuiting on the role would show the delegate a full admin
  // UI whose every call the server then refuses.
  if (role === 'tenant_owner' && !isDelegatedSession()) return true;
  if (!effectivePermissions) return false;
  return permissions.some(p => effectivePermissions.includes(p));
}

/**
 * Route-to-permission mapping for sidebar visibility and route guards.
 * More-specific paths must be listed before generic ones.
 */
export const ROUTE_PERMISSIONS = {
  '/dashboard': [],
  // Contract sub-actions (checked before the generic /contracts entry)
  '/contracts/new':  ['create_contract'],
  '/contracts/edit': ['edit_contract'],
  '/contracts':      ['view_contracts'],
  // Feature pages
  '/contract-templates': ['manage_contract_templates'],
  // General Template Library (browse & download standard contract templates)
  '/template-library':   ['view_template_library', 'manage_template_library'],
  '/schemas':            ['manage_field_schemas'],
  '/approvals':          ['approve_contract'],
  '/activity-report':    ['view_activity_report'],
  '/flow-management':    ['manage_workflows'],
  // Any per-parameter manage permission opens the Definitions panel; each tab
  // inside additionally gates on its own permission.
  '/admin':              [
    'manage_contract_main_groups', 'manage_contract_types',
    'manage_counterparty_types', 'manage_counterparties',
    'manage_currencies', 'manage_supporting_doc_types',
    'manage_field_schemas', 'manage_contract_templates',
    'manage_template_library', 'manage_settings',
  ],
  '/hr':                 ['manage_users', 'manage_departments', 'manage_positions_titles', 'manage_user_groups', 'manage_companies'],
  '/audit':              ['access_audit_logs'],
  '/permissions':        ['manage_roles'],
  '/groups':             ['manage_user_groups'],
  '/signature-integrations': ['manage_signature_integrations'],
  '/task-reassignment':  ['reassign_tasks'],
  '/subscription':       ['manage_subscription'],
  '/documents':          ['view_document_library'],
  '/branding-studio':    ['manage_branding'],
  // Workspace/tenant settings (Organization & Branding) — admin only.
  '/settings':           ['manage_settings'],
  // Personal account settings (Profile, Security, Language) — every user.
  '/profile':            [],
};

/**
 * Check if user can access a route.
 * Pass effectivePermissions to honour group-granted permissions.
 */
export function canAccessRoute(role, path, effectivePermissions = null) {
  // Deep links may carry a tab query (e.g. /hr?tab=groups) — permissions are
  // keyed by the base path.
  const requiredPerms = ROUTE_PERMISSIONS[(path || '').split('?')[0]];
  if (!requiredPerms || requiredPerms.length === 0) return true;
  return hasAnyPermission(role, requiredPerms, effectivePermissions);
}
